import { QueryClient } from '@tanstack/react-query';
import { XMLParser, XMLValidator } from 'fast-xml-parser';
import Client, {
	ClientWealthInsuranceCommonDetails,
	ClientScopeOfAdvice,
	ClientWealthRisk,
	FamilyInformation,
	FamilyMember,
	GoalInformation,
	IncomeAndExpenseInformation,
	PersonalFinance,
	RetirementPlan,
	WealthInformation,
	WealthRiskProfile,
	ClientWealthAcknowledgement,
	ClientWealthAuthorisation,
	PersonalDocumentsInformation,
	ClientEstateRelationship,
	ProfileSubsectionCompletionDetails
} from '../models/Client';
import SurveyAnswer, { SurveyUniqueIdWithAnswer } from '../models/SurveyAnswer';
import { createQueryString } from '../lib/utils';
import { SurveyResultEmailDetails } from '../models/EmailDetails';
import TwoFactorAuthenticationResult from '../models/TwoFactorAuthenticationResult';
import {
	EmailModel,
	ResetPasswordModel,
	BiometricUniqueIdModel,
	PasswordModel,
	MobileNumberConfirmationModel,
	SecurityTokenModel,
	SmsSecurityCodeVerificationModel,
	RegisterV2Model
} from '../models/AccountModels';
import { Director, DirectorWithContact, DirectorWithOfficeDetails, Office } from '../models/officeAndDirectors';
import Survey from '../models/Survey';
import ClientAndSurveyResult from '../models/ClientAndSurveyResult';
import ClientContextDetails, {
	ClientEstateProfile,
	ClientPersonalProfile,
	ClientWealthProfile
} from '../models/ClientContextDetails';
import { InvoiceDetails, UnpaidInvoice } from '../models/Invoice';
import { BusinessDetails } from '../models/Entity';
import { get, httpDelete, post, put } from './api/ApiWrapper';
import { Question } from '../models/QuestionModels';
import ProfileCompletion from '../models/ProfileCompletion';
import { ApiResult } from '../models/ApiResult';
import { SubscriptionLevels, UserOfficeCountries } from './enums';
import {
	clientContextQueryKey,
	familyMembersQueryKey,
	isSurveyEmailSentQueryKey,
	staleTimeForSession,
	staleTimeInADay,
	staleTimeInTwoHours,
	surveyAnswerQueryKey,
	surveyAnswersQueryKey,
	surveysQueryKey
} from './reactQueryCore';
import useCustomQuery from './useCustomQuery';
import useCustomMutation from './useCustomMutation';
import { PodcastItem } from '../appComponents/pages_v2/podcastV2/podcastData';
import { isLoggedIn, setAppUserOfficeCountryToLocalStorage } from './localStorage';

export const officesQueryKey = ['offices'];
export const directorsQueryKey = ['directors'];

const getQuestionsData = async (): Promise<Question[]> => {
	return get('bboc/questions', null, false);
};
export const useQuestionsData = () => {
	return useCustomQuery({
		queryKey: ['bboc', 'questions'],
		queryFn: getQuestionsData,
		staleTime: staleTimeInADay,
		refetchOnMount: false,
		refetchOnReconnect: false,
		refetchOnWindowFocus: false
	});
};

const getOffices = async (): Promise<Office[]> => {
	return get('offices', null, false);
};
export const useOffices = () => {
	return useCustomQuery({
		queryKey: officesQueryKey,
		queryFn: getOffices,
		staleTime: staleTimeForSession,
		refetchOnMount: false,
		refetchOnReconnect: false,
		refetchOnWindowFocus: false
	});
};

const getDirectors = async (): Promise<Director[]> => {
	return get('directors', null, false);
};
export const useDirectors = () => {
	return useCustomQuery({
		queryKey: directorsQueryKey,
		queryFn: getDirectors,
		staleTime: staleTimeForSession,
		refetchOnMount: false,
		refetchOnReconnect: false,
		refetchOnWindowFocus: false
	});
};

export const useDirectorContactDetails = () => {
	return useCustomQuery({
		queryKey: ['directors', 'contactDetails'],
		queryFn: async () => {
			const result: DirectorWithOfficeDetails = await get('directors/contactDetails');
			// Store the user's office country directly
			const userCountry = result?.country ?? UserOfficeCountries.AU;
			setAppUserOfficeCountryToLocalStorage(userCountry);
			return result;
		},
		staleTime: staleTimeForSession,

		refetchOnMount: false,
		refetchOnReconnect: false,
		refetchOnWindowFocus: false,
		enabled: isLoggedIn()
	});
};

export const getClientByUniqueId = async (clientUniqueId: string, signal?: AbortSignal): Promise<Client> => {
	return get(`clients/${clientUniqueId}`, signal, false);
};

export const getClientIsBusinessOwnerAndDirector = async (
	surveyUniqueId: string,
	signal?: AbortSignal
): Promise<{ isBusinessOwner: boolean; director: DirectorWithContact }> => {
	const qs = createQueryString({ surveyUniqueId });
	return get(`clients/isBusinessOwnerAndDirector${qs}`, signal, false);
};

export const createNewClient = async (
	client: Client,
	signal?: AbortSignal
): Promise<{ clientUniqueId: string; surveyUniqueId: string }> => {
	return post('clients', client, signal, false);
};

export const updateClient = async (clientUniqueId: string, client: Client, signal?: AbortSignal): Promise<string> => {
	return put(`clients/${clientUniqueId}`, client, signal, false);
};

export const useSurveyIsEmailSent = (surveyUniqueId: string) => {
	return useCustomQuery({
		queryKey: isSurveyEmailSentQueryKey(surveyUniqueId),
		queryFn: (): Promise<boolean> => get(`surveys/${surveyUniqueId}/IsEmailSent`, null, false),
		staleTime: staleTimeForSession,
		enabled: !!surveyUniqueId
	});
};

export const getSurveyAnswer = async (
	surveyUniqueId: string,
	questionUniqueName: string,
	signal?: AbortSignal
): Promise<SurveyAnswer> => {
	return get(`surveys/${surveyUniqueId}/surveyAnswers/${questionUniqueName}`, signal, false);
};
export const useSurveyAnswer = (surveyUniqueId: string, questionUniqueName: string) => {
	return useCustomQuery({
		queryKey: surveyAnswerQueryKey(surveyUniqueId, questionUniqueName),
		queryFn: (): Promise<SurveyAnswer | ''> =>
			get(`surveys/${surveyUniqueId}/surveyAnswers/${questionUniqueName}`, null, false),
		staleTime: staleTimeForSession,
		enabled: !!surveyUniqueId && !!questionUniqueName
	});
};

export const useSurveyAnswers = (surveyUniqueId: string) => {
	return useCustomQuery({
		queryKey: surveyAnswersQueryKey(surveyUniqueId),
		queryFn: (): Promise<SurveyAnswer[]> => get(`surveys/${surveyUniqueId}/surveyAnswers`, null, false),
		staleTime: staleTimeForSession,
		enabled: !!surveyUniqueId
	});
};

export const useCreateNewSurveyAnswer = (queryClient: QueryClient, onSuccess: (answerValues: unknown) => void) => {
	return useCustomMutation({
		mutationFn: (model: SurveyUniqueIdWithAnswer): Promise<void> =>
			post(
				`surveyAnswers${createQueryString({ surveyUniqueId: model.surveyUniqueId })}`,
				model.surveyAnswer,
				null,
				false
			),
		onSuccess: async (data: unknown, variables: SurveyUniqueIdWithAnswer) => {
			await queryClient.invalidateQueries({ queryKey: surveysQueryKey });
			await queryClient.invalidateQueries({ queryKey: surveyAnswersQueryKey(variables.surveyUniqueId) });
			await queryClient.invalidateQueries({
				queryKey: surveyAnswerQueryKey(variables.surveyUniqueId, variables.surveyAnswer.questionUniqueName)
			});
			onSuccess(variables.answerValues);
		}
	});
};

export const useUpdateSurveyAnswer = (queryClient: QueryClient, onSuccess: (answerValues: unknown) => void) => {
	return useCustomMutation({
		mutationFn: (model: SurveyUniqueIdWithAnswer): Promise<void> =>
			put(
				`surveyAnswers/${model.surveyAnswer.clientSurveyAnswerId}${createQueryString({ surveyUniqueId: model.surveyUniqueId })}`,
				model.surveyAnswer,
				null,
				false
			),
		onSuccess: async (data: unknown, variables: SurveyUniqueIdWithAnswer) => {
			await queryClient.invalidateQueries({ queryKey: surveysQueryKey });
			await queryClient.invalidateQueries({ queryKey: surveyAnswersQueryKey(variables.surveyUniqueId) });
			await queryClient.invalidateQueries({
				queryKey: surveyAnswerQueryKey(variables.surveyUniqueId, variables.surveyAnswer.questionUniqueName)
			});
			onSuccess(variables.answerValues);
		}
	});
};

const sendSurveyResultEmail = async (emailDetails: SurveyResultEmailDetails, signal?: AbortSignal): Promise<void> => {
	return post(`emailNotifications/surveyResult`, emailDetails, signal, false);
};
export const useSendSurveyResultEmail = (queryClient: QueryClient, onSuccess?: () => void) => {
	return useCustomMutation({
		mutationFn: (emailDetails: SurveyResultEmailDetails): Promise<void> => sendSurveyResultEmail(emailDetails),
		onSuccess: async (data: unknown, variables: SurveyResultEmailDetails) => {
			await queryClient.invalidateQueries({ queryKey: surveysQueryKey });
			await queryClient.invalidateQueries({ queryKey: isSurveyEmailSentQueryKey(variables.surveyUniqueId) });
			await queryClient.invalidateQueries({ queryKey: clientContextQueryKey });
			onSuccess?.();
		}
	});
};

export const skipBiometricLoginSetup = async (signal?: AbortSignal): Promise<void> => {
	return put('api/authentication/biometricSetupSkip', null, signal);
};

export const updateBiometricUniqueId = async (
	uniqueId: BiometricUniqueIdModel,
	signal?: AbortSignal
): Promise<void> => {
	return put('api/authentication/biometricUniqueId', uniqueId, signal);
};

export const deleteBiometricUniqueId = async (
	uniqueId: BiometricUniqueIdModel,
	signal?: AbortSignal
): Promise<void> => {
	return post('api/authentication/biometricUniqueId', uniqueId, signal);
};

export const verifyPassword = async (model: PasswordModel, signal?: AbortSignal): Promise<boolean> => {
	const response = await post('api/authentication/passwordVerification', model, signal);
	return response || null;
};

export const sendSmsSecurityCode = async (
	model: MobileNumberConfirmationModel,
	signal?: AbortSignal
): Promise<ApiResult<string>> => {
	const response = await post('api/authentication/sms/sendCode', model, signal, false);
	return response || null;
};

export const verifySmsSecurityCode = async (
	model: SmsSecurityCodeVerificationModel,
	signal?: AbortSignal
): Promise<ApiResult<string | TwoFactorAuthenticationResult>> => {
	const response = await post('api/authentication/sms/verifyCode', model, signal, false);
	return response || null;
};

export const sendSmsSecurityCodeToAuthorisedUser = async (signal?: AbortSignal): Promise<ApiResult<string>> => {
	const response = await post('api/authentication/authorised/sms/sendCode', null, signal);
	return response || null;
};

export const verifySmsSecurityCodeAndUnlockProfile = async (
	otp: string,
	signal?: AbortSignal
): Promise<ApiResult<string | TwoFactorAuthenticationResult>> => {
	const qs = createQueryString({ otp });
	const response = await post(`api/authentication/authorised/sms/verifyCodeAndUnlockProfile${qs}`, null, signal);
	return response || null;
};

export const removeRefreshToken = async (): Promise<void> => {
	return post('api/authentication/logout', null);
};

export const deleteAccount = async (): Promise<void> => {
	return httpDelete('api/authentication/account');
};

export const registerV2User = async (register: RegisterV2Model): Promise<ApiResult<SecurityTokenModel | string>> => {
	return post('api/authentication/v2/register', register, null, false);
};

export const sendForgottenPasswordEmail = async (forgotPasswordModel: EmailModel): Promise<string> =>
	post('api/authentication/forgotPassword', forgotPasswordModel, null, false);

export const resetPassword = async (resetPasswordModel: ResetPasswordModel): Promise<string> => {
	return post(`api/authentication/resetPassword`, resetPasswordModel, null, false);
};

export const useSurveys = (enabled: boolean) => {
	return useCustomQuery({
		queryKey: surveysQueryKey,
		queryFn: (): Promise<Survey[]> => get(`surveys`),
		staleTime: staleTimeForSession,
		enabled
	});
};

export const createSurvey = async (signal?: AbortSignal): Promise<ClientAndSurveyResult> => {
	return post(`surveys`, null, signal);
};

export const getClientContextDetails = async (): Promise<ClientContextDetails> => {
	return get(`clients/context`);
};

export const getClientFamilyMembers = async (): Promise<FamilyMember[]> => {
	return get(`clients/familyMembers`);
};
export const useClientFamilyMembers = () => {
	return useCustomQuery({
		queryKey: familyMembersQueryKey,
		queryFn: getClientFamilyMembers,
		staleTime: staleTimeInTwoHours // Should keep the same as in fetchClientDetails
	});
};

const addOrUpdateClientFamilyMember = async (familyMember: FamilyMember): Promise<number> => {
	return post(`clients/familyMember`, familyMember);
};
export const useAddOrUpdateClientFamilyMember = (queryClient: QueryClient, onSuccess: () => Promise<void>) => {
	return useCustomMutation({
		mutationFn: (familyMember: FamilyMember) => addOrUpdateClientFamilyMember(familyMember),
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: familyMembersQueryKey });
			onSuccess();
		}
	});
};

const archiveFamilyMember = async (familyMemberClientId: number, archived: boolean): Promise<number> => {
	return put(`clients/familyMembers/${familyMemberClientId}?archived=${archived}`, null);
};
export const useArchiveFamilyMember = (queryClient: QueryClient, onSuccess: () => void) => {
	return useCustomMutation({
		mutationFn: ({ familyMemberClientId, archived }: { familyMemberClientId: number; archived: boolean }) =>
			archiveFamilyMember(familyMemberClientId, archived),
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: familyMembersQueryKey });
			onSuccess();
		}
	});
};

export const getFamilyCommonDetails = async (signal?: AbortSignal): Promise<FamilyInformation> => {
	return get(`clients/familyCommonDetails`, signal);
};

export const updateFamilyCommonDetails = async (
	familyInformation: FamilyInformation,
	signal?: AbortSignal
): Promise<number> => {
	return put(`clients/familyCommonDetails`, familyInformation, signal);
};

export const getEstateRelationshipCommonDetails = async (signal?: AbortSignal): Promise<ClientEstateRelationship> => {
	return get(`clients/estateRelationshipCommonDetails`, signal);
};

export const updateEstateRelationshipCommonDetails = async (
	relationship: ClientEstateRelationship,
	signal?: AbortSignal
): Promise<void> => {
	return put(`clients/estateRelationshipCommonDetails`, relationship, signal);
};

export const getClientPersonalFinance = async (signal?: AbortSignal): Promise<PersonalFinance> => {
	return get(`clients/personalFinance`, signal);
};

export const updateClientPersonalFinance = async (
	personalFinance: PersonalFinance,
	signal?: AbortSignal
): Promise<void> => {
	return put(`clients/personalFinance`, personalFinance, signal);
};

export const updateClientPersonalProfile = async (
	personalProfile: ClientPersonalProfile,
	signal?: AbortSignal
): Promise<void> => {
	return put(`clients/personalProfile`, personalProfile, signal);
};

export const updateClientWealthProfile = async (
	wealthProfile: ClientWealthProfile,
	signal?: AbortSignal
): Promise<void> => {
	return put(`clients/wealthProfile`, wealthProfile, signal);
};

export const updateClientEstateProfile = async (
	estateProfile: ClientEstateProfile,
	signal?: AbortSignal
): Promise<void> => {
	return put(`clients/estateProfile`, estateProfile, signal);
};

export const getUnpaidInvoices = async (signal?: AbortSignal): Promise<UnpaidInvoice[]> => {
	return get('invoices?status=Unpaid', signal);
};

export const getInvoice = async (id: string, signal?: AbortSignal): Promise<InvoiceDetails> => {
	return get(`invoices/${id}`, signal);
};

export const getProfileCompletionPercentage = async (): Promise<ProfileCompletion[]> => {
	return get('clients/profileCompletionPercentage');
};

// Client Goals

export const getGoalCommonDetails = async (signal?: AbortSignal): Promise<GoalInformation> => {
	return get(`clients/GoalCommonDetails`, signal);
};

export const updateGoalCommonDetails = async (
	goalInformation: GoalInformation,
	signal?: AbortSignal
): Promise<number> => {
	return put(`clients/GoalCommonDetails`, goalInformation, signal);
};

// Retirement

export const getRetirementPlan = async (signal?: AbortSignal): Promise<RetirementPlan> => {
	return get(`clients/RetirementPlan`, signal);
};

export const updateRetirementPlan = async (retirementPlan: RetirementPlan, signal?: AbortSignal): Promise<number> => {
	return put(`clients/RetirementPlan`, retirementPlan, signal);
};

// Client NetWealth

export const getWealthCommonDetails = async (signal?: AbortSignal): Promise<WealthInformation> => {
	return get(`clients/WealthCommonDetails`, signal);
};

export const updateWealthCommonDetails = async (
	wealthInformation: WealthInformation,
	signal?: AbortSignal
): Promise<number> => {
	return put(`clients/WealthCommonDetails`, wealthInformation, signal);
};

// Income & Expenses

export const getIncomeAndExpenseCommonDetails = async (signal?: AbortSignal): Promise<IncomeAndExpenseInformation> => {
	return get(`clients/IncomeAndExpenseCommonDetails`, signal);
};

export const updateIncomeAndExpenseCommonDetails = async (
	incomeAndExpenseInformation: IncomeAndExpenseInformation,
	signal?: AbortSignal
): Promise<number> => {
	return put(`clients/IncomeAndExpenseCommonDetails`, incomeAndExpenseInformation, signal);
};

// Business Entities

export const getBusinessDetails = async (id: number, signal?: AbortSignal): Promise<BusinessDetails> => {
	return get(`entities/${id}/businessDetails`, signal);
};

export const updateBusinessDetails = async (
	id: number,
	businessDetails: BusinessDetails,
	signal?: AbortSignal
): Promise<number> => {
	return put(`entities/${id}/businessDetails`, businessDetails, signal);
};

// Wealth Risks

export const getClientWealthRiskDetails = async (signal?: AbortSignal): Promise<ClientWealthRisk> => {
	return get('clients/ClientWealthRisks', signal);
};

export const updateClientWealthRiskDetail = async (item: ClientWealthRisk, signal?: AbortSignal): Promise<number> => {
	return put('clients/ClientWealthRisks', item, signal);
};

export const getWealthRiskProfiles = async (signal?: AbortSignal): Promise<WealthRiskProfile[]> => {
	return get('clients/WealthRiskProfiles', signal);
};

export const updateWealthRiskProfile = async (item: WealthRiskProfile, signal?: AbortSignal): Promise<number> => {
	return put('clients/WealthRiskProfiles', item, signal);
};

// Insurance common details

export const getWealthInsuranceCommonDetails = async (
	signal?: AbortSignal
): Promise<ClientWealthInsuranceCommonDetails> => {
	return get('clients/WealthInsurancesCommonDetails', signal);
};

export const updateWealthInsuranceCommonDetail = async (
	item: ClientWealthInsuranceCommonDetails,
	signal?: AbortSignal
): Promise<number> => {
	return put('clients/WealthInsurancesCommonDetails', item, signal);
};

export const getClientScopeOfAdvice = async (signal?: AbortSignal): Promise<ClientScopeOfAdvice> => {
	return get('clients/ClientScopeOfAdvice', signal);
};

export const getClientWealthAcknowledgement = async (signal?: AbortSignal): Promise<ClientWealthAcknowledgement> => {
	return get('clients/ClientWealthAcknowledgementDetails', signal);
};

export const updateClientWealthAcknowledgementDetail = async (
	item: ClientWealthAcknowledgement,
	signal?: AbortSignal
): Promise<number> => {
	return put('clients/ClientWealthAcknowledgementDetails', item, signal);
};

export const getClientWealthAuthorisation = async (signal?: AbortSignal): Promise<ClientWealthAuthorisation> => {
	return get('clients/ClientWealthAuthorisationDetails', signal);
};

export const updateClientWealthAuthorisationDetail = async (
	item: ClientWealthAuthorisation,
	signal?: AbortSignal
): Promise<number> => {
	return put('clients/ClientWealthAuthorisationDetails', item, signal);
};

export const getClientPersonalDocumentsInformation = async (
	signal?: AbortSignal
): Promise<PersonalDocumentsInformation> => {
	return get('clients/PersonalDocumentsInformation', signal);
};

export const updateClientPersonalDocumentsInformation = async (
	item: PersonalDocumentsInformation,
	signal?: AbortSignal
): Promise<number> => {
	return put('clients/PersonalDocumentsInformation', item, signal);
};

export const getProfileSubsectionCompletionDetail = async (
	section: string,
	entityId?: number,
	signal?: AbortSignal
): Promise<ProfileSubsectionCompletionDetails[]> => {
	return get(
		`clients/ProfileSubsectionCompletion?section=${section}${entityId ? `&entityId=${entityId}` : ''}`,
		signal
	);
};

export const updateClientPersonalSubscriptionLevel = async (
	subscriptionLevel: SubscriptionLevels,
	signal?: AbortSignal
) => {
	return put(`clients/PersonalSubscriptionLevel`, { subscriptionLevel }, signal);
};

const getEpisodesData = async () => {
	const response = await fetch('https://bebetteroffshow.libsyn.com/rss/?include-libsyn-metadata=true');
	const text = await response.text();

	if (!XMLValidator.validate(text)) {
		throw new Error('Invalid RSS episodes XML data.');
	}

	const options = { attributeNamePrefix: '', ignoreAttributes: false, removeNSPrefix: true };
	const parser = new XMLParser(options);
	const parsedData = parser.parse(text);

	if (!parsedData?.rss?.channel?.item) {
		return []; // Return an empty array if no items are found
	}

	const rssItems = Array.isArray(parsedData.rss.channel.item)
		? parsedData.rss.channel.item
		: [parsedData.rss.channel.item];
	const transformedPodcasts: PodcastItem[] = rssItems.map(
		(item: {
			title: string | string[];
			subtitle: string;
			pubDate: string;
			description: string;
			duration: string;
			itemId: string;
			image?: { href: string };
		}) => {
			const { title, subtitle, pubDate, description, duration, itemId, image } = item;
			return {
				title: Array.isArray(title) ? title[0] : title, // Handle title as array or string
				subtitle,
				pubDate,
				image: image ? { href: image.href } : undefined, // Safely handle image object
				description,
				duration,
				itemId
			};
		}
	);

	return transformedPodcasts;
};
export const useEpisodesData = () => {
	return useCustomQuery({
		queryKey: ['podcasts', 'allEpisodes'],
		queryFn: getEpisodesData,
		staleTime: staleTimeInTwoHours,
		refetchOnMount: false,
		refetchOnReconnect: false,
		refetchOnWindowFocus: false
	});
};
